import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  DOCUMENT,
  ElementRef,
  inject,
  Input,
  OnInit,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NowFilterDropdown } from '@now/dpl';
import { delay, filter, firstValueFrom, Observable, of, take, withLatestFrom } from 'rxjs';
import { BasisFacetteService } from '../../../services/facetten/instance/basis-facette.service';
import { LoaderService } from '../../../services/loader.service';
import { ScreenSizeService, ViewMode } from '../../../services/screen-size';

@Component({
  selector: 'now-wbsuche-lib-basis-facette',
  templateUrl: './basis-facette.component.html',
  styleUrls: ['./basis-facette.component.scss'],
  imports: [CommonModule]
})
export class BasisFacetteComponent implements OnInit, AfterViewInit {
  private readonly screenSizeService = inject(ScreenSizeService);
  private readonly document = inject<Document>(DOCUMENT);
  loaderService = inject(LoaderService);

  @Input({ required: true })
  id!: string;

  @Input({ required: true })
  label!: string;

  @Input({ required: true })
  facettenService!: BasisFacetteService;

  @Input()
  isLoading: boolean = false;

  @ViewChild('facetteContainer')
  facetteContainer!: ElementRef<HTMLElement>;

  @ViewChild('toggle')
  toggleElement!: ElementRef<HTMLElement>;

  @ViewChild('dropdown')
  dropdown!: ElementRef<HTMLElement>;

  @ViewChild('mobileAccordionItem')
  mobileAccordionItem!: ElementRef;

  isDesktop$ = this.screenSizeService.rangeFrom$(ViewMode.DESKTOP);

  selectedFilterCount$?: Observable<number>;

  private preventClose = false;
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.listenOnModals();
    this.selectedFilterCount$ = this.facettenService.getNumberSelected$();
    this.setExpansion(false);
  }

  ngAfterViewInit(): void {
    if (this.dropdown) {
      const dropdown = NowFilterDropdown.init(this.dropdown.nativeElement, {
        autoClose: 'outside',
        display: 'static',
        isAvailableOnBootstrapInit: false
      });

      dropdown.addDropdownEventListener('shown', () => {
        this.setExpansion(true);
      });

      // TODO: aktivieren, wenn NOW das Event mit in die Callback Funktion gibt
      // dropdown.addDropdownEventListener('hide', (event: Event) => {
      //   this.preventClose ? event.preventDefault() : this.setExpansion(false);
      // });
      // TODO deaktivieren, wenn eventlistener drüber funktioniert!
      this.toggleElement?.nativeElement.addEventListener('hide.bs.dropdown', (event: Event) => {
        this.preventClose ? event.preventDefault() : this.setExpansion(false);
      });
    }
  }

  // Listen on Modal hidden and shown events to prevent Closing of the facetten when the modal was closed
  private listenOnModals() {
    // Subscribe only the first time, the facette gets opened
    this.facettenService.expanded$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(Boolean),
        // delay wird benötigt um sicher zu stellen dass das Modal gerendert ist, nachdem es geöffnet wurde
        delay(200),
        take(1),
        withLatestFrom(this.isDesktop$),
        filter(([_, isDesktop]) => isDesktop)
      )
      .subscribe(() => {
        const modalTriggers = this.facetteContainer.nativeElement.querySelectorAll(
          '[data-bs-toggle="modal"]'
        );
        modalTriggers.forEach((modalTrigger) => {
          const modalId = (modalTrigger as HTMLElement).dataset.bsTarget!.replace('#', '');
          const modal = this.document.getElementById(modalId);
          modal?.addEventListener('hidden.bs.modal', () => {
            this.preventClose = false;
          });
          modal?.addEventListener('shown.bs.modal', () => {
            this.preventClose = true;
          });
        });
      });
  }

  public async toggleExpansion(): Promise<void> {
    const expanded = await firstValueFrom(this.facettenService.expanded$);
    this.setExpansion(!expanded);

    if (!expanded && this.mobileAccordionItem) {
      this.scrollMobileAccordionIntoView();
    }
  }

  private scrollMobileAccordionIntoView(): void {
    of(null)
      // Delay um sicherzustellen, dass der Content der Facette bereits gerendert wurde
      .pipe(delay(300))
      .subscribe(() => {
        this.mobileAccordionItem.nativeElement.scrollIntoView({ behavior: 'smooth' });
      });
  }

  private setExpansion(expanded: boolean): void {
    this.facettenService.setExpanded(expanded);
  }
}
